import request from 'axios';
import queryString from 'query-string';
import { DEFAULT_PAGINATION_PAGE_SIZE } from 'common/pager/constants';

/* ACTIONS
================================================================================================ */
const CLEAR_PARENT_PAGE = 'crowdspring/admin_verification/CLEAR_PARENT_PAGE';

const FETCH_PARENT_PAGE = 'crowdspring/admin_verification/FETCH_PARENT_PAGE';
const FETCH_PARENT_PAGE_SUCCESS = 'crowdspring/admin_verification/FETCH_PARENT_PAGE_SUCCESS';

const FETCH_WARNINGS = 'crowdspring/admin_verification/FETCH_WARNINGS';
const FETCH_WARNINGS_REQUEST = 'crowdspring/admin_verification/FETCH_WARNINGS_REQUEST';
const FETCH_WARNINGS_SUCCESS = 'crowdspring/admin_verification/FETCH_WARNINGS_SUCCESS';

const TOGGLE_PROCESS_ID_MODAL = 'crowdspring/admin_verification/TOGGLE_PROCESS_ID_MODAL';

const CLEAR_WORK_SAMPLES = 'crowdspring/admin_verification/CLEAR_WORK_SAMPLES';

const FETCH_IMAGE_SAMPLES = 'crowdspring/admin_verification/FETCH_IMAGE_SAMPLES';
const FETCH_IMAGE_SAMPLES_SUCCESS = 'crowdspring/admin_verification/FETCH_IMAGE_SAMPLES_SUCCESS';

const FETCH_WRITING_SAMPLES = 'crowdspring/admin_verification/FETCH_WRITING_SAMPLES';
const FETCH_WRITING_SAMPLES_SUCCESS = 'crowdspring/admin_verification/FETCH_WRITING_SAMPLES_SUCCESS';

const TOGGLE_PROCESS_SAMPLES_MODAL = 'crowdspring/admin_verification/TOGGLE_PROCESS_SAMPLES_MODAL';

const TOGGLE_REPLACE_IMG_MODAL = 'crowdspring/admin_verification/TOGGLE_REPLACE_IMG_MODAL';

/* HELPERS
================================================================================================ */
const processParentPageData = (action) => {
  const updatedState = {
    previous: null,
    next: null,
    current: null,
    totalCount: 0
  };
  const currentRelativeIdx = action.payload.results.findIndex(u => u.id === parseInt(action.userId));

  if (currentRelativeIdx < 0) {
    return updatedState;
  }

  updatedState.totalCount = action.payload.count;

  updatedState.current = {
    idx: action.offset + currentRelativeIdx
  };

  if (currentRelativeIdx > 0) {
    updatedState.previous = {
      id: action.payload.results[currentRelativeIdx - 1].id,
      idx: updatedState.current.idx - 1
    };
  }

  if (currentRelativeIdx < action.payload.results.length - 1) {
    updatedState.next= {
      id: action.payload.results[currentRelativeIdx + 1].id,
      idx: updatedState.current.idx + 1
    };
  }

  return updatedState;
}

export const rejectIdValidationRequest = (userId, data) => (
  request.put(`/api/v1/radmin/users/${userId}/reject_id_validation/`, data)
);

export const approveIdValidationRequest = (userId, data) => (
  request.put(`/api/v1/radmin/users/${userId}/approve_id_validation/`, data)
);

export const processSamplesRequest = (userId, data) => (
  request.put(`/api/v1/radmin/users/${userId}/process_work_samples/`, data)
);

export const replaceFieldRequest = (userId, data) => (
  request.put(`/api/v1/radmin/users/${userId}/replace_creative_verification_field/`, data)
);

const formatSamples = (payload) => {
  return payload.filter(s => !!s.subcategory_group_id).reduce((acc, s) => ({
    ...acc,
    [s.subcategory_group_id]: s
  }), {});
}

/* INITIAL STATES
================================================================================================ */
const initialState = {
  previous: null,
  next: null,
  current: null,
  totalCount: 0,
  warnings: [],
  showingProcessIdModal: false,
  workSamples: {},
  showingProcessSamplesModal: false
};

/* REDUCERS
================================================================================================ */
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_PARENT_PAGE_SUCCESS:
      return {
        ...state,
        ...processParentPageData(action)
      };
    case CLEAR_PARENT_PAGE:
      return {
        ...state,
        previous: null,
        next: null,
        current: null,
        totalCount: 0
      };
    case FETCH_WARNINGS_REQUEST:
      return {
        ...state,
        warnings: []
      };
    case FETCH_WARNINGS_SUCCESS:
      return {
        ...state,
        warnings: action.payload.warnings.concat(action.payload.errors)
      };
    case TOGGLE_PROCESS_ID_MODAL:
      return {
        ...state,
        showingProcessIdModal: !state.showingProcessIdModal
      };
    case CLEAR_WORK_SAMPLES:
      return {
        ...state,
        workSamples: {}
      };
    case FETCH_IMAGE_SAMPLES_SUCCESS:
      return {
        ...state,
        workSamples: {
          ...state.workSamples,
          ...formatSamples(action.payload)
        }
      };
    case FETCH_WRITING_SAMPLES_SUCCESS:
      return {
        ...state,
        workSamples: {
          ...state.workSamples,
          ...formatSamples(action.payload)
        }
      };
    case TOGGLE_PROCESS_SAMPLES_MODAL:
      return {
        ...state,
        showingProcessSamplesModal: !state.showingProcessSamplesModal
      };
    case TOGGLE_REPLACE_IMG_MODAL:
      return {
        ...state,
        replacingImgFor: action.payload
      };
    default:
      return state;
  }
}

export const clearParentPage = () => dispatch => (
  dispatch({
    type: CLEAR_PARENT_PAGE
  })
);

export const fetchParentPage = (userId, userIdx, listQuery) => dispatch => {
  /* `userIdx` is just an estimate of where user with ID `userId` may be found within
     the list. That's why we use the position found in `processParentPageData()` instead and
     use a page as large as possible below. */
  const offset = Math.max(userIdx - DEFAULT_PAGINATION_PAGE_SIZE/2, 0);
  const limit = DEFAULT_PAGINATION_PAGE_SIZE;
  const query = queryString.stringify({
    ...listQuery,
    offset,
    limit
  });

  return dispatch({
    type: FETCH_PARENT_PAGE,
    promise: request.get(`/api/v1/users/?${query}`),
    userId,
    offset,
  });
};

export const fetchWarnings = (userId) => dispatch => {
  return dispatch({
    type: FETCH_WARNINGS,
    promise: request.get(`/api/v1/users/${userId}/registration_fraud_warnings/`),
  });
};

export const toggleProcessIdModal = () => dispatch => (
  dispatch({
    type: TOGGLE_PROCESS_ID_MODAL
  })
);

export const fetchImageSamples = (userId) => (dispatch) => {
  return dispatch({
    type: FETCH_IMAGE_SAMPLES,
    promise: request.get(`/api/v1/users/${userId}/get_image_samples/`)
  });
}

export const fetchWritingSamples = (userId) => (dispatch) => {
  return dispatch({
    type: FETCH_WRITING_SAMPLES,
    promise: request.get(`/api/v1/users/${userId}/get_writing_samples/`)
  });
}

export const clearWorkSamples = () => dispatch => (
  dispatch({
    type: CLEAR_WORK_SAMPLES
  })
);

export const toggleProcessSamplesModal = () => dispatch => (
  dispatch({
    type: TOGGLE_PROCESS_SAMPLES_MODAL
  })
);

export const toggleReplaceImgModal = (imageFor) => dispatch => (
  dispatch({
    type: TOGGLE_REPLACE_IMG_MODAL,
    payload: imageFor
  })
);



// WEBPACK FOOTER //
// ./admin/ducks/verification.js